// Message thread: chat surface, prose, code/diff cards, user bubble,
// attachments, feedback row, thinking dots
// (WEB-8005). Composed by praxisStyles.ts at the section's original
// position — source order is part of the cascade.
export const praxisMessages = `
/* Chat surface */
.pr-chat-main { background: var(--pr-surface); }

.pr-avatar-sm {
  width: 28px; height: 28px;
  border-radius: 7px;
  background: var(--pr-ink);
  color: white;
  display: grid; place-items: center;
  flex-shrink: 0;
  overflow: hidden;
  font-family: var(--pr-font-serif);
  font-weight: 600;
  font-size: 13px;
}
.pr-avatar-sm img { width: 100%; height: 100%; object-fit: cover; display: block; }
/* When an image is present, drop the dark ink fallback background so it
   can't show as an anti-aliased rim around the rounded corners. */
.pr-avatar-sm:has(img) { background: transparent; }
.pr-brand-dot:has(img) { background: transparent; }
.pr-avatar-lg:has(img) { background: transparent; }

.pr-assistant-name {
  font-size: 13px; font-weight: 600; color: var(--pr-ink);
}
.pr-msg-time {
  font-family: var(--pr-font-mono); font-size: 11px;
  color: var(--pr-ink-4);
  font-variant-numeric: tabular-nums;
}

/* Prose (serif headings, monospace code, accent quotes) */
.pr-prose { font-size: 14px; line-height: 1.65; color: var(--pr-ink); }
.pr-prose > *:first-child { margin-top: 0; }
.pr-prose > *:last-child { margin-bottom: 0; }
.pr-prose p { margin: 8px 0; }
.pr-prose h1, .pr-prose h2 {
  font-family: var(--pr-font-serif);
  font-weight: 500; letter-spacing: -0.01em; color: var(--pr-ink);
}
.pr-prose h1 { font-size: 20px; margin: 18px 0 8px; line-height: 1.25; }
.pr-prose h2 { font-size: 17px; margin: 16px 0 6px; line-height: 1.3; }
.pr-prose h3 {
  font-size: 11.5px; margin: 14px 0 6px;
  font-family: var(--pr-font-ui);
  letter-spacing: 0.04em; font-weight: 600;
  color: var(--pr-ink-3);
  text-transform: uppercase;
}
.pr-prose ul, .pr-prose ol { padding-left: 0; margin: 8px 0; list-style: none; }
.pr-prose ul li, .pr-prose ol li { margin: 4px 0; }
.pr-prose ul li { position: relative; padding-left: 18px; }
.pr-prose ul li::before {
  content: ""; position: absolute; left: 2px; top: 0.72em;
  width: 6px; height: 1px; background: var(--pr-ink-3);
}
.pr-prose ol { counter-reset: item; }
.pr-prose ol li {
  counter-increment: item;
  position: relative; padding-left: 24px;
}
.pr-prose ol li::before {
  content: counter(item) ".";
  position: absolute; left: 0; top: 0;
  font-family: var(--pr-font-mono); font-variant-numeric: tabular-nums;
  font-size: 12px; color: var(--pr-accent);
  font-weight: 500;
}
.pr-prose code {
  font-family: var(--pr-font-mono); font-size: 12.5px;
  background: var(--pr-surface-2); padding: 1px 5px;
  border-radius: 4px;
  border: 1px solid var(--pr-hairline);
  color: var(--pr-ink);
}
/* Code blocks render as a dark "editor window". A bare <pre> (inbox previews,
   embedded drawer) gets the dark body; where MessageBlock wraps the block in
   .pr-code it also gains the chrome bar (window dots, language pill, copy). */
.pr-prose pre {
  font-family: var(--pr-font-mono); font-size: 12.5px; line-height: 1.6;
  background: oklch(0.21 0.014 255); color: oklch(0.90 0.012 255);
  border: 1px solid oklch(0.30 0.02 255);
  padding: 14px 16px; border-radius: var(--pr-r-md);
  overflow-x: auto; margin: 12px 0;
  box-shadow: var(--pr-shadow-sm);
  -webkit-font-smoothing: antialiased;
}
.pr-prose pre code { background: transparent; padding: 0; border: none; color: inherit; font-size: inherit; }

.pr-prose .pr-code {
  /* Light frame matching the diff card (.pr-diff) so both code surfaces read as
     one component family (WEB-7821). */
  margin: 12px 0;
  border: 1px solid oklch(0.80 0.012 255);
  border-radius: var(--pr-r-md, 10px);
  overflow: hidden;
  background: var(--pr-surface, oklch(1 0 0));
  box-shadow: var(--pr-shadow-sm, 0 1px 2px oklch(0.22 0.012 250 / 0.06));
}
.pr-prose .pr-code pre {
  margin: 0; border: none; border-radius: 0; box-shadow: none; max-height: 360px; overflow: auto;
  background: var(--pr-surface, oklch(1 0 0));
  color: oklch(0.35 0.015 255);
  font-size: 12.5px; line-height: 1.55;
  padding: 10px 12px;
}
.pr-code-bar {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px;
  background: var(--pr-surface-2, oklch(0.975 0.004 250));
  border-bottom: 1px solid oklch(0.80 0.012 255);
}
.pr-code-dots { display: inline-flex; gap: 6px; }
.pr-code-dots i { width: 10px; height: 10px; border-radius: 50%; }
.pr-code-dots i:nth-child(1) { background: #ff5f57; }
.pr-code-dots i:nth-child(2) { background: #febc2e; }
.pr-code-dots i:nth-child(3) { background: #28c840; }
.pr-code-lang {
  font-family: var(--pr-font-mono, ui-monospace, Menlo, monospace); font-size: 10.5px; font-weight: 500;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: var(--pr-ink-3, oklch(0.50 0.010 250));
}
.pr-code-copy {
  margin-left: auto;
  display: inline-flex; align-items: center; gap: 5px;
  background: transparent;
  border: 1px solid var(--pr-border-2, oklch(0.82 0.006 250));
  color: var(--pr-ink-3, oklch(0.50 0.010 250));
  font-family: var(--pr-font-ui, "Inter", -apple-system, Helvetica, Arial, sans-serif); font-size: 11px; font-weight: 500;
  padding: 3px 9px; border-radius: 6px; cursor: pointer;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;
}
.pr-code-copy svg { width: 12px; height: 12px; }
.pr-code-copy:hover {
  background: var(--pr-surface-2, oklch(0.975 0.004 250));
  color: var(--pr-ink, oklch(0.22 0.012 250));
  border-color: var(--pr-ink-4, oklch(0.62 0.010 250));
}

/* Editor-style change view for a fenced diff block (WEB-7757): a bordered card
   with a title bar (window dots + "N lines changed"), collapsed behind that bar
   by default and expanding to red/green line tints. Rules are GLOBAL (not
   .pr-prose-scoped) so the frame renders wherever the block appears — the tint
   rules were already global, which is why they showed but the frame didn't. */
/* Dual selectors (.pr-prose .pr-diff … , .pr-diff …): the .pr-prose-scoped one is
   (0,2,x) so it beats the generic .pr-prose pre / .pr-prose code editor styles
   regardless of source order; the bare one covers a diff rendered outside prose.
   Mirrors how .pr-code resets its inner pre. */
.pr-prose .pr-diff,
.pr-diff {
  /* var() fallbacks are mandatory here: the Pages authoring chat renders the
     diff OUTSIDE a .praxis ancestor, so the --pr-* tokens are undefined there.
     Without fallbacks border-radius/background collapse to initial values →
     square corners + transparent header (WEB-7757). */
  margin: 12px 0;
  border: 1px solid oklch(0.80 0.012 255);
  border-radius: var(--pr-r-md, 10px);
  overflow: hidden;
  background: var(--pr-surface, oklch(1 0 0));
  box-shadow: var(--pr-shadow-sm, 0 1px 2px oklch(0.22 0.012 250 / 0.06));
}
.pr-diff-summary {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px; cursor: pointer; user-select: none;
  background: var(--pr-surface-2, oklch(0.975 0.004 250));
  font-family: var(--pr-font-ui, "Inter", -apple-system, Helvetica, Arial, sans-serif); font-size: 12px; font-weight: 500;
  color: var(--pr-ink, oklch(0.22 0.012 250)); list-style: none;
}
.pr-diff-summary::-webkit-details-marker { display: none; }
.pr-diff-summary:hover { background: oklch(0.95 0.005 255); }
.pr-diff-summary:focus, .pr-diff-summary:focus-visible { outline: none; }
.pr-diff[open] .pr-diff-summary { border-bottom: 1px solid oklch(0.80 0.012 255); }
.pr-diff .pr-code-dots { display: inline-flex; gap: 6px; flex: none; }
.pr-diff .pr-code-dots i { width: 9px; height: 9px; border-radius: 50%; }
.pr-diff .pr-code-dots i:nth-child(1) { background: #ff5f57; }
.pr-diff .pr-code-dots i:nth-child(2) { background: #febc2e; }
.pr-diff .pr-code-dots i:nth-child(3) { background: #28c840; }
.pr-diff-caret {
  width: 0; height: 0; flex: none;
  border-top: 4px solid transparent; border-bottom: 4px solid transparent;
  border-left: 5px solid oklch(0.60 0.02 255);
  transition: transform 120ms ease;
}
.pr-diff[open] .pr-diff-caret { transform: rotate(90deg); }
.pr-diff-title { color: oklch(0.45 0.02 255); }
/* Full-rewrite badge — flags a whole-file rewrite (vs a targeted edit) so it's
   obvious the moment the agent commits to one. Amber = attention, not error (WEB-7756). */
.pr-diff-tag {
  flex: none; padding: 1px 7px; border-radius: 999px;
  font-size: 10px; font-weight: 600; line-height: 16px; letter-spacing: 0.02em;
  text-transform: uppercase;
  color: oklch(0.42 0.11 65); background: oklch(0.93 0.06 75);
}
/* New-page variant — a first build is expected, so it's neutral/informational
   rather than the amber attention of an unexpected full rewrite (WEB-7756). */
.pr-diff-tag--new {
  color: oklch(0.42 0.03 255); background: oklch(0.92 0.015 255);
}
.pr-diff-body {
  padding: 0;
  background: var(--pr-surface, oklch(1 0 0)); color: var(--pr-ink, oklch(0.22 0.012 250));
  /* Default shows ~10 lines then scrolls — a full rewrite is hundreds of lines
     and shouldn't dominate the thread. Drag the .pr-diff-resize handle to grow. */
  max-height: 210px; overflow: auto;
}
/* Full-width drag handle across the card bottom, with a centred grip pill so
   it reads as draggable. Drives handleDiffResizeStart (WEB-7757). */
.pr-diff-resize {
  height: 11px; cursor: ns-resize;
  display: flex; align-items: center; justify-content: center;
  background: var(--pr-surface-2, oklch(0.975 0.004 250));
  border-top: 1px solid oklch(0.90 0.008 255);
}
.pr-diff-grip {
  width: 30px; height: 3px; border-radius: 2px;
  background: oklch(0.78 0.014 255);
  transition: background 120ms ease, width 120ms ease;
}
.pr-diff-resize:hover .pr-diff-grip { background: #1D3D48; width: 40px; }
/* Each diff row is a flex pair: a fixed, neutral line-number gutter + the code
   text. The gutter is user-select:none so copying the block never drags numbers
   along; add/del tinting lives on the text so the gutter stays calm (WEB-7821). */
.pr-diff-line {
  display: flex; align-items: flex-start;
  font-family: var(--pr-font-mono, "JetBrains Mono", ui-monospace, Menlo, monospace); font-size: 12.5px; line-height: 1.55;
  color: oklch(0.50 0.015 255);
}
.pr-diff-num {
  flex: none; width: 2.75em; padding: 1px 10px 1px 0; text-align: right;
  color: oklch(0.74 0.012 255); user-select: none; -webkit-user-select: none;
}
.pr-diff-text {
  flex: 1 1 auto; min-width: 0;
  white-space: pre-wrap; word-break: break-word; overflow-wrap: anywhere;
  padding: 1px 12px 1px 4px;
}
.pr-diff-line.pr-diff-add .pr-diff-text { background: oklch(0.93 0.06 150); color: oklch(0.38 0.09 150); }
.pr-diff-line.pr-diff-del .pr-diff-text { background: oklch(0.94 0.05 25); color: oklch(0.45 0.13 25); }
/* Divider between edit hunks — a faint dashed rule so several scattered changes in
   one turn read as separate blocks, not one merged wall (WEB-7756). */
.pr-diff-line.pr-diff-sep {
  display: block; padding: 0; height: 0; margin: 12px 12px;
  border-top: 1px dashed oklch(0.82 0.012 255);
}
.pr-code.is-copied .pr-code-copy { color: oklch(0.78 0.15 155); border-color: oklch(0.50 0.12 155); }


/* While a reply streams, the in-progress code/diff card pulses instead of
   dangling the block caret beneath it, and hides the copy button until done.
   Covers .pr-diff too, else the caret ▊ leaks below the diff card and doubles
   up with the streaming dots (WEB-7757). */
.pr-asst-body.pr-caret:has(.pr-code:last-child)::after,
.pr-asst-body.pr-caret:has(.pr-diff:last-child)::after { display: none; }
.pr-caret .pr-code:last-child { border-color: oklch(0.50 0.10 245 / 0.6); }
.pr-caret .pr-code:last-child .pr-code-copy { display: none; }
.pr-caret .pr-code:last-child .pr-code-bar::after {
  content: ""; margin-left: auto;
  width: 7px; height: 7px; border-radius: 50%;
  background: oklch(0.64 0.14 245);
  animation: pr-codepulse 1.1s ease-in-out infinite;
}
@keyframes pr-codepulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(0.7); }
}
.pr-prose blockquote {
  border-left: 2px solid var(--pr-accent);
  padding: 2px 0 2px 14px; margin: 12px 0;
  color: var(--pr-ink-2);
  font-family: var(--pr-font-serif); font-style: italic;
  font-size: 15px; line-height: 1.55;
}
.pr-prose a {
  color: var(--pr-accent-ink);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  text-decoration-color: var(--pr-accent-soft-2);
  cursor: pointer;
  transition: text-decoration-color 120ms ease;
}
.pr-prose a:hover { text-decoration-color: var(--pr-accent); }
.pr-prose hr { border: none; border-top: 1px solid var(--pr-hairline); margin: 14px 0; }
.pr-prose img {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 10px 0;
  border-radius: 8px;
  border: 1px solid var(--pr-hairline);
  cursor: zoom-in;
  transition: opacity 120ms ease;
}
.pr-prose img:hover { opacity: 0.9; }
.pr-prose table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 13px; }
.pr-prose th, .pr-prose td { border-bottom: 1px solid var(--pr-hairline); padding: 8px 10px; text-align: left; border-top: none; border-left: none; border-right: none; }
.pr-prose th { font-weight: 600; color: var(--pr-ink-3); font-size: 11.5px; letter-spacing: 0.02em; background: transparent; }
.pr-prose td { font-variant-numeric: tabular-nums; }
.pr-prose a.pr-entity-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 0 5px; margin: 0;
  border-radius: 4px;
  background: var(--pr-accent-soft);
  color: var(--pr-accent-ink);
  border: 1px solid var(--pr-accent-soft-2);
  font-size: 13px; font-weight: 500;
  line-height: 1.4;
  text-decoration: none;
  vertical-align: baseline;
  transition: background 120ms ease;
}
.pr-prose a.pr-entity-chip:hover { background: var(--pr-accent-soft-2); }
.pr-prose a.pr-mention-link { cursor: pointer; }
/* Colour swatch rendered inline before a hex code in agent output. */
.pr-prose .pr-color-swatch {
  display: inline-block;
  width: 1.05em; height: 1.05em;
  margin-right: 0.4em;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.18);
  vertical-align: -0.2em;
}

.pr-asst-msg { position: relative; }
.pr-asst-head {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 4px 0;
  margin-bottom: 4px;
}
.pr-asst-head .pr-avatar-sm { margin-top: -2px; }
.pr-asst-body {
  /* Flush-left: body, code and the action row all share the avatar's left margin
     rather than indenting under the name — nothing hangs out to the side (WEB-7756). */
  padding-left: 0;
}

/* No-name messages (e.g. page-authoring chat): lay avatar + body side-by-side
   so the first line of body content sits on the avatar row instead of
   dropping below it. */
.pr-asst-msg--inline {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.pr-asst-msg--inline .pr-asst-head {
  padding: 0;
  margin-bottom: 0;
  flex-shrink: 0;
}
.pr-asst-msg--inline .pr-asst-head .pr-avatar-sm { margin-top: 0; }
.pr-asst-msg--inline .pr-asst-body {
  flex: 1;
  min-width: 0;
  padding-left: 0;
}

/* User bubble */
.pr-user-bubble {
  background: var(--pr-accent-soft);
  color: var(--pr-ink);
  border: 1px solid var(--pr-accent-soft-2);
  border-radius: var(--pr-r-lg) var(--pr-r-lg) 4px var(--pr-r-lg);
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.55;
  /* Don't let long unbroken tokens push the bubble past its max-width. */
  overflow-wrap: anywhere;
}
.pr-user-bubble > p { margin: 0; }
.pr-user-bubble > p + p { margin-top: 8px; }
.pr-user-bubble a {
  display: inline;
  padding: 1px 6px;
  margin: 0 1px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.07);
  color: inherit;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: background 120ms ease;
}
.pr-user-bubble a:hover { background: rgba(0, 0, 0, 0.12); }

/* Non-image attachment chips shown above the user bubble */
.pr-msg-attachment-link { text-decoration: none; color: inherit; }
.pr-msg-attachment {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 10px 6px 6px;
  background: var(--pr-surface);
  border: 1px solid var(--pr-border);
  border-radius: 10px;
  max-width: 240px;
}
.pr-msg-attachment .ws-ctx-thumb {
  width: 32px; height: 32px; border-radius: 6px;
  display: grid; place-items: center;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.04em; color: white;
  flex-shrink: 0;
}
.pr-msg-attachment .ws-ctx-thumb.pdf { background: #d44b4b; }
.pr-msg-attachment .ws-ctx-thumb.xlsx { background: #2c8e5f; }
.pr-msg-attachment .ws-ctx-thumb.image { background: #6f6ee0; }
.pr-msg-attachment .ws-ctx-thumb.pptx { background: #c75b3a; }
.pr-msg-attachment .ws-ctx-thumb.text { background: #6b7280; }
.pr-msg-attachment-info { display: flex; flex-direction: column; min-width: 0; }
.pr-msg-attachment-name {
  font-size: 12.5px; font-weight: 500; color: var(--pr-ink);
  white-space: nowrap; text-overflow: ellipsis; overflow: hidden;
}
.pr-msg-attachment-size { font-size: 11px; color: var(--pr-ink-4); }

/* Feedback */
.pr-feedback { display: flex; align-items: center; gap: 2px; }
/* Offset the row so the first icon's visible glyph — not the button's
   padded hit area — aligns with the message text above. 42px avatar+gap
   minus the button's inner 7px of padding. */
.pr-feedback-row { margin-left: 0; }
.pr-fbtn {
  background: transparent; border: none;
  width: 34px; height: 34px; border-radius: 7px;
  display: grid; place-items: center;
  color: var(--pr-ink-5);
  cursor: pointer;
  transition: all 120ms ease;
}
.pr-fbtn:hover { color: var(--pr-ink); background: var(--pr-surface-2); }
.pr-fbtn.active { color: var(--pr-accent); background: var(--pr-accent-soft); }
.pr-feedback-divider { width: 1px; height: 12px; background: var(--pr-hairline); margin: 0 4px; }

/* Thinking dots */
.pr-thinking { display: flex; align-items: flex-start; gap: 10px; max-width: 760px; margin: 0 auto; padding: 0 28px; }
/* Inline variant — drops the self-centering so it inherits the parent column
   (used inside RunSection, where MessageThread already centres the 720px track). */
.pr-thinking--inline { max-width: none; margin: 0; padding: 0; gap: 10px; }
.pr-thinking .pr-avatar-sm { opacity: 0.55; margin-top: -2px; }
.pr-thinking-body { display: flex; flex-direction: column; gap: 6px; padding-top: 6px; }
.pr-dots { display: inline-flex; gap: 4px; align-items: center; }
.pr-dots span {
  width: 5px; height: 5px; border-radius: 50%;
  background: var(--pr-ink-3);
  animation: pr-bounce 1.2s infinite ease-in-out;
}
.pr-dots span:nth-child(2) { animation-delay: 150ms; }
.pr-dots span:nth-child(3) { animation-delay: 300ms; }
@keyframes pr-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-3px); opacity: 1; }
}
.pr-tool-status {
  font-size: 12px; color: var(--pr-ink-3);
  display: inline-flex; align-items: center; gap: 8px;
}
.pr-tool-status .pr-spin {
  width: 10px; height: 10px;
  border: 1.5px solid var(--pr-accent); border-top-color: transparent;
  animation: pr-spin 600ms linear infinite;
  border-radius: 50%;
}
@keyframes pr-spin { to { transform: rotate(360deg); } }
.pr-caret::after {
  content: "";
  display: inline-block;
  width: 7px; height: 14px;
  background: var(--pr-ink);
  margin-left: 2px; vertical-align: -2px;
  animation: pr-blink 1.06s ease-in-out infinite;
  border-radius: 1px;
}
@keyframes pr-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.15; } }
`;
